import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from "../environments/environment";
import { mayaConfig } from '../environments/environment.myparking';

@Injectable({
  providedIn: 'root',
})
export class PayMayaService {

  // Get current environment configuration
  private currentConfig = environment.production ? mayaConfig.production : mayaConfig.sandbox;
  
  // Boolean to indicate if we're in sandbox mode (opposite of production)
  private isSandbox = !environment.production;

  constructor(private http: HttpClient) {
    
  }

  // Method to get the current environment's base URL
  private getBaseUrl(): string {
    return this.currentConfig.baseUrl;
  }

  // Method to get the current environment's API credentials
  private getSecretKey(): string {
    const credentials = btoa(`${this.currentConfig.publicKey}:${this.currentConfig.secretKey}`);
    return credentials;
  }

  // Method to get current environment info
  getEnvironmentInfo(): { environment: string; isSandbox: boolean; baseUrl: string } {
    return {
      environment: this.isSandbox ? 'sandbox' : 'production',
      isSandbox: this.isSandbox,
      baseUrl: this.currentConfig.baseUrl
    };
  }

  // Method to validate current configuration
  validateConfiguration(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!this.currentConfig.publicKey || this.currentConfig.publicKey.length < 10) {
      errors.push('Invalid or missing public key');
    }
    
    if (!this.currentConfig.secretKey || this.currentConfig.secretKey.length < 10) {
      errors.push('Invalid or missing secret key');
    }
    
    if (!this.currentConfig.baseUrl || !this.currentConfig.baseUrl.startsWith('https://')) {
      errors.push('Invalid or missing base URL');
    }
    
    // Check if using correct URLs for environment
    const expectedSandboxUrl = 'https://pg-sandbox.paymaya.com';
    const expectedProdUrl = 'https://pg.paymaya.com';
    
    if (this.isSandbox && this.currentConfig.baseUrl !== expectedSandboxUrl) {
      errors.push(`Sandbox should use ${expectedSandboxUrl}`);
    }
    
    if (!this.isSandbox && this.currentConfig.baseUrl !== expectedProdUrl) {
      errors.push(`Production should use ${expectedProdUrl}`);
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Method to create a checkout (automatically uses current environment)
  createCheckout(payload: any): Observable<any> {
    const envInfo = this.getEnvironmentInfo();

    const encodedCredentials = this.getSecretKey();
    const url = `${this.getBaseUrl()}/checkout/v1/checkouts`;

    const headers = new HttpHeaders({
      Authorization: `Basic ${encodedCredentials}`,
      'Content-Type': 'application/json',
    });

    // Ensure requestReferenceNumber is set
    payload.requestReferenceNumber = payload.reference;

    return this.http.post(url, payload, { headers });
  }

  // Method to generate a basic checkout payload for testing
  generateCheckoutPayload(currentInvoice: any, arrears: number): any {
    if (!currentInvoice) {
      console.error('No current invoice found');
      return null;
    }

    // Extracting invoice data to generate checkout payload
    const { monthlyDue, ownerFullName, ownerPhoneNumber, ownerEmail, referenceNumber } = currentInvoice;

    // Assuming owner's name is in "First Last" format, split to get first and last name.
    const [firstName, lastName] = ownerFullName.split(' ');

    // Calculate total amount including monthly due and arrears
    const totalAmountDue = monthlyDue + arrears;

    return {
      totalAmount: {
        value: totalAmountDue,  // Total amount to be paid (monthly due + arrears)
        currency: 'PHP'
      },
      buyer: {
        firstName: firstName || '',  // Fallback in case name is not available
        lastName: lastName || '',
        contact: {
          phone: ownerPhoneNumber || '',  // Replace with the actual phone number
          email: ownerEmail || '',  // Replace with the actual email if needed
        },
      },
      items: [
        {
          name: 'Homeowners monthly due',
          quantity: 1,
          totalAmount: {
            value: monthlyDue,
            currency: 'PHP',
          },
        },
        {
          name: 'Arrears',
          quantity: 1,
          totalAmount: {
            value: arrears,
            currency: 'PHP',
          },
        },
      ],
      reference: referenceNumber,
      redirectUrl: {
        success: `${window.location.origin}/myparking/payment-status/success`,
        failure: `${window.location.origin}/myparking/payment-status/failure`,
        cancel: `${window.location.origin}/myparking/payment-status/cancel`,
      },
    };
  }

}
